import moment from 'moment';
import PersonMixin from '@/modules/Application/mixins/PersonMixin';
import api from '../../../../../configurations/service';
import * as types from './mutation-types';
import { stdObjectForSave } from '../parsers/save-purchase';
import { stdObjectForEdit } from '../parsers/edit-purchase';
import { stdObjectForEditFromXml } from '../parsers/xml/import-xml';

const { formatPerson } = PersonMixin.methods;

export const setUnitOfMeasureById = async (context, unitId) => {
  const body = { id: unitId };
  const { data } = await api().post('inventory.get-unit_of_measure', body);
  context.commit(
    types.SET_UNIT_OF_MEASURE,
    data.find((e) => e.id === unitId),
  );
};

export const setCsosnById = async (context, csosnId) => {
  if (!csosnId) return;
  const body = { id: csosnId };
  const { data } = await api().post('fiscal.get-csosn-by-id', body);
  context.commit(types.SET_CSOSN_FOR_PRODUCT, data);
};

export const setCenqById = async (context, cenqId) => {
  if (!cenqId) return;
  const body = { id: cenqId };
  const { data } = await api().post('fiscal.get-cenq-by-id', body);
  context.commit(types.SET_CENQ_FOR_PRODUCT, data);
};

export const setOrigemById = async (context, origemId) => {
  if (!origemId) return;
  const body = { id: origemId };
  const { data } = await api().post('fiscal.get-origem-by-id', body);
  context.commit(types.SET_ORIGEM_FOR_PRODUCT, data);
};

export const setCstById = async (context, cstId) => {
  if (!cstId) return;
  const body = { id: cstId };
  const { data } = await api().post('fiscal.get-cst-by-id', body);
  context.commit(types.SET_CST_FOR_PRODUCT, data);
};

export const setCstIpiById = async (context, cstIPIId) => {
  if (!cstIPIId) return;
  const body = { id: cstIPIId };
  const { data } = await api().post('fiscal.get-cstipi-by-id', body);
  context.commit(types.SET_CST_IPI_FOR_PRODUCT, data);
};

export const setCstPisById = async (context, cstPISId) => {
  if (!cstPISId) return;
  const body = { id: cstPISId };
  const { data } = await api().post('fiscal.get-cstpis-by-id', body);
  context.commit(types.SET_CST_PIS_FOR_PRODUCT, data);
};

export const setCstCofinsById = async (context, cstCOFINSId) => {
  if (!cstCOFINSId) return;
  const body = { id: cstCOFINSId };
  const { data } = await api().post('fiscal.get-cstcofins-by-id', body);
  context.commit(types.SET_CST_COFINS_FOR_PRODUCT, data);
};

export const setCfopByIdForProduct = async (context, cfopId) => {
  if (!cfopId) return context.commit(types.SET_CFOP_FOR_PRODUCT, []);
  const body = { id: cfopId };
  const { data } = await api().post('fiscal.get-cfop-by-id', body);
  return context.commit(types.SET_CFOP_FOR_PRODUCT, data);
};

export const setNfeAndPurchaseCollection = async (context) => {
  const rootState = { ...context.rootState };
  const { nfeCollection } = rootState.nfe;
  const { purchaseCollection } = rootState.purchase;

  const nfeCollectionFormatted = nfeCollection.data.map((nfe) => ({
    nfeId: nfe.id,
    number: nfe.numero,
    model: nfe.modelo,
    serie: nfe.serie || '',
    docKey: nfe.chave,
  }));

  const purchaseCollectionFormatted = purchaseCollection.map((purchase) => ({
    purchaseId: purchase.id,
    number: purchase.eletronicInvoiceNumber,
    model: purchase.nfeModel,
    serie: purchase.nfeSerie || '',
    docKey: purchase.nfeKey,
  }));

  const nfeAndPurchaseCollection = [...nfeCollectionFormatted, ...purchaseCollectionFormatted];

  return nfeAndPurchaseCollection;
};

export const setPurchaseCollection = async (context, search, haveNfeKey = true) => {
  const nfeNumber = Number(search);
  if (!nfeNumber) return;
  const body = { maxResults: 10, nfeNumber, haveNfeKey };
  const { data } = await api().post('fiscal.get-purchase-by-refNFe', body);
  context.commit(types.SET_PURCHASE_COLLECTION, data);
};

export const setSummaryPurchases = async (context, { search, pagination }) => {
  const body = {
    search,
    pagination,
  };
  const { data } = await api().post('consumers.find-summary-purchases', body);

  if (data?.pagination) {
    context.commit(types.SET_PAGINATION_PURCHASE, data.pagination);
  } else {
    context.commit(types.SET_PAGINATION_PURCHASE, {
      ...pagination,
      totalItems: 0,
    });
  }

  context.commit(types.SET_SUMMARY_PURCHASES, data.data ?? []);
};

export const getPendingManifestos = async () => {
  const { data } = await api().post('fiscal.get-pending-manifestos');
  return data ?? [];
};

export const findPendingPurchases = async () => {
  const { data } = await api().post('consumers.find-pending-purchases');
  return data ?? [];
};

export const setPurchase = async (context) => {
  context.commit(types.SET_PURCHASE_TO_SAVE);
};

export const setShippingCompanyById = async (context, transport) => {
  const transportId = transport.shippingCompany.id;
  const body = { id: transportId, maxResults: 1 };
  const { data } = await api().post('person.get-person', body);
  context.commit(types.SET_SHIPPING_COMPANY, data[0]);
};

export const resetPurchase = async (context) => {
  context.commit(types.RESET_PURCHASE);
};

export const totalCalcPurchase = async ({ getters, commit, dispatch }, params) => {
  await dispatch('setPurchase');
  const { getCurrentPurchase } = getters;
  const standardizedPurchase = stdObjectForSave(getCurrentPurchase);
  const purchase = {
    ...standardizedPurchase,
    taxCalc: {
      ...standardizedPurchase.taxCalc,
      calcTypes: params.calcTypes,
    },
  };
  const { data } = await api().post('consumers.total-calc-purchase', purchase);
  commit(types.SET_TOTAL_CALC_PURCHASE, data);
};

export const updatePurchaseData = ({ commit }, payload) =>
  commit(types.SET_PURCHASE_FOR_EDIT, payload);

export const updateImportedInvoiceData = ({ commit }, payload) =>
  commit(types.SET_IMPORTED_INVOICE, payload);

export const savePurchase = async ({ getters }, purchase) => {
  const { importedInvoiceFormData } = getters;
  const standardizedPurchase = stdObjectForSave(purchase);
  const { data } = await api().post('consumers.register-purchase', standardizedPurchase);

  if (importedInvoiceFormData && importedInvoiceFormData instanceof FormData) {
    const formData = new FormData();
    formData.append('file', importedInvoiceFormData.get('file'));
    formData.append('filename', data.id);

    await api().post('consumers.upload-purchase-xml', formData);

    return data;
  }

  if (standardizedPurchase.manifestoId) {
    await api().post('fiscal.upload-manifesto-into-purchase', {
      id: standardizedPurchase.manifestoId,
      purchaseId: data.id,
    });

    return data;
  }

  return data;
};

export const calculateProductsCost = async (context, purchase) => {
  const { data } = await api().post('consumers.calculate-products-cost', purchase);

  return data;
};

export const updatePurchase = async (_, purchase) => {
  const standardizedPurchase = stdObjectForSave(purchase);
  await api().post('consumers.update-purchase', standardizedPurchase);
};

export const deletePurchase = async (_, purchaseIds) => {
  await api().post('consumers.delete-purchase', { purchaseIds });
};

export const setPurchaseFromImport = async (context, payload) => {
  context.commit(types.SET_PURCHASE_FOR_EDIT, {
    ...payload,
    taxInformation: {
      ...payload.taxInformation,
      entryDate: new Date(),
    },
  });
};

export const setPayment = async (context, payment) => {
  context.commit(types.SET_PAYMENT, payment);
};

export const setInstallmentsValues = async (context, calculatedRange) => {
  if (!Array.isArray(calculatedRange) || !calculatedRange.length) return;

  const newRange = calculatedRange.map((e) => ({
    ...e,
    value: e.value,
  }));

  context.commit(types.SET_INSTALLMENTS_RANGE, newRange);
};

export const setInstallmentsDates = async (context, calculatedRange) => {
  if (!Array.isArray(calculatedRange) || !calculatedRange.length) return;

  const newRange = calculatedRange.map((e) => ({
    ...e,
    date: new Date(moment(e.date)),
  }));

  context.commit(types.SET_INSTALLMENTS_RANGE, newRange);
};

export const setLinkedProducts = async ({ commit, getters }, productInStock) => {
  const { productOfPurchaseCollectionXmlImportation } = getters;
  const linkedProductOfPurchaseCollection = productOfPurchaseCollectionXmlImportation.map(
    (e, index) => {
      const product = e;
      if (productInStock[index]) {
        const productId = productInStock[index].productId ?? productInStock[index].id;
        product.productId = productId;
        product.description = productInStock[index].description;
      }

      return product;
    },
  );

  commit(types.SET_LINKED_PRODUCTS, linkedProductOfPurchaseCollection);
};

export const importXml = async (context, formData) => {
  const config = { headers: { 'Content-Type': 'multipart/form-data' } };
  const { data } = await api().post('consumers.import-xml', formData, config);
  const standardizedXml = await stdObjectForEditFromXml(data);
  context.commit(types.SET_IMPORTED_INVOICE, standardizedXml);
  context.commit(types.SET_IMPORTED_INVOICE_FORM_DATA, formData);
};

export const importManifestoIntoPurchase = async (context, manifesto) => {
  const { data } = await api().post('fiscal.import-manifesto-into-purchase', { id: manifesto.id });
  if (!data) return;
  const standardizedXml = await stdObjectForEditFromXml(data);
  context.commit(types.SET_IMPORTED_INVOICE, { ...standardizedXml, manifestoId: manifesto.id });
  context.commit(types.SET_IMPORTED_INVOICE_FORM_DATA, manifesto);
};

export const clearXmlFormData = async (context) => {
  context.commit(types.SET_IMPORTED_INVOICE_FORM_DATA, null);
};

export const setProductOfPurchaseOnFiscalEdit = async (context, formattedValues) => {
  context.commit(types.SET_PRODUCT_OF_PURCHASE_ON_FISCAL_EDIT, formattedValues);
};

export const setRemainder = async ({ getters, commit }) => {
  const { totalPurchase, paymentModeCollection, productOfPurchaseCollection } = getters;

  if (productOfPurchaseCollection <= 0) commit(types.SET_REMAINDER, 0);

  const totalPayment = paymentModeCollection.reduce(
    (accumulator, item) => accumulator + (item.value ?? 0),
    0,
  );
  const remainder = totalPurchase - totalPayment;

  if (remainder <= 0) {
    commit(types.SET_REMAINDER, 0);
  } else {
    commit(types.SET_REMAINDER, remainder);
  }
};

export const setSupplier = async (context, params) => {
  context.commit(types.SET_SUPPLIER, params);
};

export const setSupplierError = async (context, error) => {
  context.commit(types.SET_SUPPLIER_ERROR, error);
};

export const setNaturezaError = async (context, error) => {
  context.commit(types.SET_NATUREZA_ERROR, error);
};

export const setCfopCollectionForProductPurchase = async (context, params) => {
  const { supplierId, search } = params;
  const body = { active: true, search, supplierId };
  if (!supplierId) return;

  const { data } = await api().post('consumers.get-cfop-collection-for-purchase', body);
  context.commit(types.SET_CFOP_COLLECTION, data);
};

export const setMatchedCfopForPurchaseTaxInformation = async (context, params) => {
  const { supplierId, cfopIdPurchaseHeader } = params;
  const body = { supplierId, cfopIdPurchaseHeader };
  const { data } = await api().post('consumers.get-matched-cfop-for-purchase', body);
  context.commit(types.SET_MATCHED_CFOP_FOR_PURCHASE_TAX_INFORMATION, data);
};

export const setDetailedPurchase = async (context, purchaseId) => {
  const { data } = await api().post('consumers.find-detailed-purchase', { purchaseId });
  const dataSupplierFormatted = formatPerson(data.supplier);
  data.supplier = dataSupplierFormatted;
  const standardizedPurchase = await stdObjectForEdit(data);
  context.commit(types.SET_PURCHASE_FOR_EDIT, standardizedPurchase);

  if (!data.taxInformation.natureza) return;

  await setMatchedCfopForPurchaseTaxInformation(context, {
    supplierId: data.supplier.id,
    cfopIdPurchaseHeader: data.taxInformation.natureza?.cfop?.id,
  });
};

export const setMatchedCfopForProductPurchase = async (context, params) => {
  const { supplierId, cfopIdPurchaseHeader } = params;
  const body = { supplierId, cfopIdPurchaseHeader };
  const { data } = await api().post('consumers.get-matched-cfop-for-purchase', body);
  context.commit(types.SET_MATCHED_CFOP_FOR_PRODUCT_PURCHASE, data);
};

export const setDecimalConfiguration = async (context) => {
  const { data } = await api().postWithCache('application.get-client');
  const decimalConfiguration = {
    price: data.fiscal.emissor.decimalPrice,
    quantity: data.fiscal.emissor.decimalQuantity,
  };
  context.commit(types.SET_DECIMAL_CONFIGURATION, decimalConfiguration);
};

export const setSelectedNatureza = async (context, selectedNatureza) => {
  context.commit(types.SET_SELECTED_NATUREZA, selectedNatureza);
};

export const setProductOnPurchaseCollection = async (context, params) => {
  const { productData, fiscal, originalIndex } = params.productOfPurchaseOnFiscalEdit;
  const { productOfPurchaseCollection } = params.products;
  const productUpdated = {
    originalIndex,
    data: {
      ...productData,
      fiscal,
      edited: true,
    },
  };
  const updatedproductOfPurchaseCollection = productOfPurchaseCollection.splice(
    productUpdated.originalIndex,
    1,
    productUpdated.data,
  );
  context.commit(types.SET_PRODUCT_ON_PURCHASE_COLLECTION, updatedproductOfPurchaseCollection);
};

export const hasInvalidCollapse = (_, { collapsesToValidate, refs, root }) =>
  collapsesToValidate.some((e) => {
    const collapseClass = refs[e].$el.children[0].children[1].classList.value;
    const isInvalidCollapse = refs[e].$v.$invalid;

    if (isInvalidCollapse && collapseClass.includes('show')) return true;
    if (isInvalidCollapse && !collapseClass.includes('show')) {
      root.$emit('bv::toggle::collapse', e);
      return true;
    }

    return false;
  });

export const updateProductsPrice = async (context, productsToUpdatePrice) =>
  api().post('consumers.purchase-update-products-price', { products: productsToUpdatePrice });
